import React, { useEffect, useState } from 'react';

import { Alert, FormGroup, Row, Col, Card, CardBody, Button, ButtonGroup } from 'reactstrap';

import { useForm, useFieldArray } from 'react-hook-form';
import ReactFlagsSelect from 'react-flags-select';
import { useSelector, useDispatch } from 'react-redux';
import _, { uniqueId } from 'lodash';
import Moment from 'react-moment';

import { capitalizeFirstLetter } from '../../utils';
import _axios, { StoreManagerApi } from '../../api';
import { SelectField, renderInputField, RHFField, ErrorMessage } from '../../components/Field';
import { domainError, stagingHostError } from './domainValidation';
import { certificateDomains, certManagedBy } from './certificateDomains';
import * as storeActions from '../../actions/storeActions';
import * as operatorActions from '../../actions/operatorActions';
import XMTable from '../../components/XMTable';

// Migrated from redux-form to react-hook-form. The `domains` redux-form FieldArray
// became a useFieldArray; redux-form's asyncValidate/asyncBlurFields for store-name
// uniqueness became an async `validate` rule on `name` (mode: 'onBlur').
//
// Domains are presented as "frontend groupings" (see the Store Manager design handoff):
// each grouping is the set of domains sharing a frontend type, carrying its own staging
// domain and its own primary. Untyped legacy domains form the implicit Default grouping.
function StoreForm({ initialValues, isEdit, handleCancel, toggle }) {
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const store = useSelector((state) => state.store);
  const operator = useSelector((state) => state.operator);
  const erpType = useSelector((state) => state.erpType);
  const dataCentre = useSelector((state) => state.dataCentre);

  const [primaryDomain, setPrimaryDomain] = useState(null);
  const [erpTypeSelected, setErpTypeSelected] = useState(null);

  const { register, control, handleSubmit, reset, setValue, getValues, watch, trigger } = useForm({
    mode: 'onBlur',
    defaultValues: { name: '', partnerUsers: null, ...initialValues },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'domains' });
  const watchedDomains = watch('domains') || [];

  // Frontend grouping types, declared per environment in the backend's
  // xmd.frontends.types config. Untyped domains keep the legacy DNS behaviour.
  // full type descriptors from xmd.frontends.types: { type, cname, isCertManager }
  const frontendTypes = (config.frontendTypes || []).map((t) => (typeof t === 'string' ? { type: t } : t));
  const frontendTypeNames = frontendTypes.map((t) => t.type);
  const certManaged = certManagedBy(frontendTypes);
  // new stores are provisioned behind this frontend (per env config); existing
  // untyped domains keep the legacy Default grouping
  const defaultFrontendType = config.defaultFrontendType || null;
  const frontendLabel = (f) => (f || '').replace('_', ' ');
  const sameGroup = (a, b) => (a.frontend || '') === (b.frontend || '');
  const stagingSuffix = config.properties['domain'];

  // Validate a domain row against the same rules the backend enforces on save, so a bad
  // domain is called out at the field instead of coming back as a failed save. Staging rows
  // are generated and read-only, so they are left to the server.
  function domainRowError(value, index, groupKey) {
    const all = getValues('domains') || [];
    if ((all[index] || {}).isStaging) return undefined;
    return domainError(value, {
      certManaged: certManaged(groupKey),
      otherNames: all.filter((_d, i) => i !== index).map((d) => (d || {}).name),
    });
  }

  // A collision message belongs to BOTH rows, but RHF only revalidates the field
  // being edited — so fixing one row left the other still accusing it. Re-run the
  // siblings' validators whenever a row changes.
  // Staging rows are excluded: domainRowError short-circuits to `true` for them, so
  // triggering one can only ever be a no-op.
  function revalidateOtherDomains(changedIndex) {
    const names = (getValues('domains') || [])
      .map((d, i) => (i === changedIndex || (d || {}).isStaging ? null : `domains.${i}.name`))
      .filter(Boolean);
    if (names.length) trigger(names);
  }

  // Group the field-array rows (index preserved for register paths) by frontend type.
  const buckets = {};
  fields.forEach((field, index) => {
    const domain = watchedDomains[index] || field;
    const key = domain.frontend || '';
    (buckets[key] = buckets[key] || []).push({ field, domain, index });
  });
  const orderedGroupKeys = [...new Set(['', ...frontendTypeNames, ...Object.keys(buckets)])].filter((k) => buckets[k]);
  const availableGroupTypes = frontendTypeNames.filter((t) => !buckets[t]);

  // add-grouping dropdown + staging-hostname prompt (per the design handoff)
  const [addOpen, setAddOpen] = useState(false);
  const [groupPrompt, setGroupPrompt] = useState(null);
  const [promptHost, setPromptHost] = useState('');
  const promptHostError = groupPrompt
    ? stagingHostError(promptHost, { certManaged: certManaged(groupPrompt), suffix: stagingSuffix })
    : undefined;

  // TLS provisioning state per live typed domain, keyed by hostname; null until fetched.
  // The staging domain rides the environment wildcard certificate and carries no badge.
  const [v5Status, setV5Status] = useState(null);
  const persistedFrontendDomains = certificateDomains(initialValues.domains, certManaged);

  // initialValues is a fresh object every parent render; reinitialize only when the
  // values actually change (matches redux-form enableReinitialize).
  const initKey = JSON.stringify(initialValues);
  useEffect(() => {
    reset({ name: '', partnerUsers: null, ...initialValues });
    dispatch(storeActions.clearError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  useEffect(() => {
    dispatch(operatorActions.getAllByRole('PARTNER'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // config.frontendTypes is a dep because persistedFrontendDomains is filtered through
  // certManaged(): if configs arrive after mount the fetch must still fire
  useEffect(() => {
    if (isEdit && initialValues.id && persistedFrontendDomains.length > 0) {
      StoreManagerApi.getDomainStatus(initialValues.id)
        .then((data) => setV5Status(data || {}))
        .catch(() => setV5Status({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initKey, config.frontendTypes]);

  function v5StatusText(name) {
    if (v5Status === null) return 'CHECKING';
    return v5Status[name] || 'NOT MANAGED';
  }

  function v5StatusBadge(state) {
    if (state === 'ACTIVE') return 'bg-success';
    if (state === 'PROVISIONING') return 'bg-warning text-dark';
    if (state === 'CHECKING' || state === 'NOT MANAGED') return 'bg-secondary';
    return 'bg-danger';
  }

  // Store-name uniqueness — HEAD /stores/{name}: 200 means taken, 404/error means
  // available. Skipped when editing and the name is unchanged.
  async function validateNameUnique(value) {
    try {
      const values = getValues();
      if (!values.id || values.originName !== value) {
        const response = await _axios.head(`/stores/${value}`);
        if (response.status === 200) return 'The store name is taken';
      }
      return true;
    } catch (err) {
      if (err && err.message) console.error(err);
      return true;
    }
  }

  function onChangeErpType(event) {
    const value = event.target.value;
    const selected = _.find(erpType.all, (o) => o.id == value);
    setErpTypeSelected(selected);
  }

  function submit(values) {
    let nextStore = {
      ...values,
      storeStatuses: null,
      dataCentre: {
        id: dataCentre.selected.id,
      },
    };

    // add 1st domain on insert
    if (!isEdit) {
      let isStgPrimary = true;

      if (!nextStore.domains) {
        nextStore.domains = [];
      }

      // the staging domain joins the default frontend grouping, so it only takes the
      // primary role if no other domain in that same grouping already holds it
      for (let i = 0; i < nextStore.domains.length; i++) {
        if (sameGroup(nextStore.domains[i], { frontend: defaultFrontendType }) && nextStore.domains[i].isPrimary) {
          isStgPrimary = false;
          break;
        }
      }

      // add stg domain
      nextStore.domains.unshift({
        name: nextStore.stgDomain,
        isStaging: true,
        isPrimary: isStgPrimary,
        frontend: defaultFrontendType,
      });
    }

    dispatch(storeActions.save(nextStore))
      .then(() => {
        toggle();
        setErpTypeSelected(null);
      })
      .catch(() => {
        // remove stg domain (ui purpose)
        if (!isEdit) {
          nextStore.domains.shift();
        }
      });
  }

  function onCancel() {
    setErpTypeSelected(null);
    handleCancel();
    toggle();
  }

  function removeDomain(index) {
    const removed = (getValues('domains') || [])[index] || {};
    remove(index);

    // keep the Default grouping owning a primary after a deletion (legacy behaviour);
    // typed groupings may be left without a primary until the user picks one
    if (isEdit && !removed.frontend) {
      const all = getValues('domains') || [];
      const firstDefault = all.findIndex((d) => !(d && d.frontend));
      all.forEach((d, i) => {
        if (!(d && d.frontend)) {
          setValue(`domains.${i}.isPrimary`, i === firstDefault);
        }
      });
      if (firstDefault !== -1) {
        setPrimaryDomain({ ...getValues(`domains.${firstDefault}`), index: firstDefault });
      }
    } else if (primaryDomain && primaryDomain.index == index) {
      setPrimaryDomain(null);
    }
  }

  function makeDomainPrimary(index) {
    const all = getValues('domains') || [];
    // one primary per frontend grouping: only domains in the same grouping are affected
    const target = index == undefined ? {} : all[index] || {};
    all.forEach((d, i) => {
      if (sameGroup(d || {}, target)) {
        setValue(`domains.${i}.isPrimary`, i == index);
      }
    });

    // Primary isnt the "sub domains"
    if (index == undefined) {
      setPrimaryDomain(null);
    } else {
      setPrimaryDomain({
        ...getValues(`domains.${index}`),
        index: index,
      });
    }
  }

  function createGrouping() {
    const host = promptHost.trim();
    if (!host) return;
    // the grouping is born with its staging domain; the modal always shows the env
    // suffix, so the created name always carries it (dots are stripped on input)
    append({
      name: host + '.' + stagingSuffix,
      isStaging: true,
      isPrimary: false,
      frontend: groupPrompt,
    });
    setGroupPrompt(null);
    setPromptHost('');
  }

  function addDomainToGroup(key) {
    append({ name: '', isStaging: false, isPrimary: false, frontend: key || null });
  }

  function removeGrouping(key) {
    (buckets[key] || [])
      .map((row) => row.index)
      .sort((a, b) => b - a)
      .forEach((i) => remove(i));
    // indices shift under the removed rows; drop the cached create-mode primary marker
    setPrimaryDomain(null);
  }

  const { one, error } = store;

  let erpTypeVersions = [];

  // if ERP is selected use the erpVersions from selected ERP
  if (erpTypeSelected) {
    erpTypeVersions = erpTypeSelected.erpTypeVersions;
  } else if (one.erpType) {
    erpTypeVersions = one.erpType.erpTypeVersions;
  }

  const erpTypeReg = register('erpType.id', { required: true });

  function renderDomainRow({ domain, field, index }, groupKey) {
    return (
      <FormGroup key={field.id} row className="mb-2">
        <Col md="3">
          <label htmlFor={`domains.${index}.name`}>
            Domain{' '}
            <span className="text-primary">
              {domain.isStaging && '(Staging) '} {domain.isPrimary && '(Primary) '}
            </span>{' '}
          </label>
        </Col>
        <Col md="9">
          <Row>
            <Col>
              <span style={{ display: 'flex' }}>
                <input
                  {...register(`domains.${index}.protocol`)}
                  disabled
                  type="text"
                  className="form-control px-1"
                  placeholder="https://"
                  style={{ maxWidth: '3.5rem' }}
                />
                <span style={{ flexGrow: 1 }}>
                  <RHFField
                    name={`domains.${index}.name`}
                    control={control}
                    component={renderInputField}
                    type="text"
                    // renderInputField prepends form-control itself now
                    className="px-1"
                    disabled={domain.isStaging}
                    placeholder="example.com"
                    rules={{ validate: (value) => domainRowError(value, index, groupKey) || true }}
                    onValueChange={() => revalidateOtherDomains(index)}
                  />
                </span>
              </span>
            </Col>
            <Col md="5" className="ps-0">
              <div className="float-end">
                {!domain.isPrimary && !domain.isStaging && (
                  <Button color="primary" onClick={() => makeDomainPrimary(index)} className="me-2">
                    Make Primary
                  </Button>
                )}
                <ButtonGroup>
                  {domain.isStaging ? (
                    <Button onClick={() => addDomainToGroup(groupKey)} color="light" title="Add domain">
                      <i className="icon-plus" />
                    </Button>
                  ) : (
                    <Button onClick={() => removeDomain(index)} color="light" title="Delete domain">
                      <i className="icon-trash" />
                    </Button>
                  )}
                </ButtonGroup>
              </div>
            </Col>
          </Row>
        </Col>
      </FormGroup>
    );
  }

  return (
    <div>
      <div>
        <form onSubmit={handleSubmit(submit)} className="form-horizontal">
          {error && <Alert color="danger">{error}</Alert>}
          <hr className="separator" />
          <input {...register('id')} type="text" className="form-control" hidden />
          <input {...register('originName')} type="text" className="form-control" hidden />
          <FormGroup row>
            <Col md="3">
              <label htmlFor="storeId">Store Id</label>
            </Col>
            <Col md="9">
              <input {...register('storeId')} type="text" className="form-control" disabled={true} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="dataCentre">Data Center</label>
            </Col>
            <Col md="9" className="align-middle">
              <ReactFlagsSelect
                rfsKey={uniqueId()}
                selected={dataCentre.selected.countryCode}
                disabled={true}
                showSelectedLabel={false}
                className={'menu-flags'}
                selectButtonClassName={'menu-flags-btn'}
                onSelect={() => void 0}
              />
              <span>{dataCentre.selected.name}</span>
            </Col>
          </FormGroup>

          <FormGroup row>
            <Col md="3">
              <label htmlFor="name">Name</label>
            </Col>
            <Col md="9">
              <RHFField
                name="name"
                control={control}
                component={renderInputField}
                type="text"
                className="form-control"
                required
                maxLength={30}
                rules={{ validate: validateNameUnique }}
              />
            </Col>
          </FormGroup>

          <div>
            {!isEdit && (
              <FormGroup row>
                <Col md="3">
                  <label htmlFor="primaryDn">
                    Domain{' '}
                    <span className="text-primary">
                      {!primaryDomain && '(Primary) '}
                      {defaultFrontendType && '(' + frontendLabel(defaultFrontendType) + ') '}
                    </span>
                  </label>
                </Col>
                <Col md="9">
                  <Row>
                    <Col>
                      <span style={{ display: 'flex' }}>
                        <input
                          {...register('protocol')}
                          disabled
                          type="text"
                          className="form-control px-1"
                          placeholder="https://"
                          style={{ maxWidth: '3.5rem' }}
                        />
                        <span style={{ flexGrow: 1 }}>
                          <RHFField
                            name="stgDomain"
                            control={control}
                            component={renderInputField}
                            type="text"
                            // renderInputField prepends form-control itself now
                            className="px-1"
                            placeholder="mystore"
                            rules={{
                              validate: (value) =>
                                stagingHostError(value, {
                                  certManaged: certManaged(defaultFrontendType || ''),
                                  suffix: stagingSuffix,
                                }) || true,
                            }}
                          />
                        </span>
                      </span>
                    </Col>
                    <Col md="7" className="ps-0">
                      <p style={{ display: 'inline-block', marginTop: '14px' }}>.{stagingSuffix}</p>
                      <div className="float-end">
                        {primaryDomain ? (
                          <Button color="primary" onClick={() => makeDomainPrimary()} className="me-2">
                            Make Primary
                          </Button>
                        ) : null}
                        <Button onClick={() => addDomainToGroup(defaultFrontendType || '')} color="light">
                          <i className="icon-plus" />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </FormGroup>
            )}

            {/* Frontend Groupings (see design handoff): each grouping has its own staging and primary domain */}
            <div className="d-flex align-items-center justify-content-between border-top pt-3 mb-3">
              <div>
                <div className="fw-bold">Frontend Groupings</div>
                <small className="text-muted">Each grouping has its own staging and primary domain.</small>
              </div>
              <div style={{ position: 'relative' }}>
                <Button
                  color="primary"
                  type="button"
                  onClick={() => setAddOpen(!addOpen)}
                  disabled={availableGroupTypes.length === 0}
                >
                  + Add new frontend grouping <span style={{ fontSize: '10px' }}>▾</span>
                </Button>
                {addOpen && (
                  <>
                    <div onClick={() => setAddOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                    <div
                      className="bg-white border rounded shadow-sm py-1"
                      style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 21, minWidth: '200px' }}
                    >
                      <div className="text-muted text-uppercase px-3 pt-1" style={{ fontSize: '11px', letterSpacing: '.5px' }}>
                        Grouping type
                      </div>
                      {availableGroupTypes.map((t) => (
                        <button
                          key={t}
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setAddOpen(false);
                            setGroupPrompt(t);
                            setPromptHost('');
                          }}
                        >
                          {frontendLabel(t)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {orderedGroupKeys.length === 0 && (
              <div
                className="rounded text-center text-muted p-4 mb-3"
                style={{ border: '1.5px dashed #d3dade', fontSize: '13px' }}
              >
                No frontend groupings yet. Use “Add new frontend grouping” to create one.
              </div>
            )}

            {orderedGroupKeys.map((key) => {
              const rows = buckets[key] || [];
              const stagingRows = rows.filter((r) => r.domain.isStaging);
              const liveRows = rows.filter((r) => !r.domain.isStaging);
              return (
                <Card key={key || 'default'} className="mb-3" style={{ background: '#fbfcfd' }}>
                  <CardBody className="py-3">
                    <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                      <div>
                        <span className="badge bg-info me-2">{key ? frontendLabel(key) : 'DEFAULT'}</span>
                        <small className="text-muted">Frontend grouping</small>
                      </div>
                      {key && (
                        <Button
                          color="link"
                          type="button"
                          className="text-muted p-0"
                          style={{ fontSize: '12px', textDecoration: 'none' }}
                          onClick={() => removeGrouping(key)}
                        >
                          <i className="icon-trash" /> Remove grouping
                        </Button>
                      )}
                    </div>
                    {stagingRows.map((row) => renderDomainRow(row, key))}
                    {stagingRows.length === 0 && (
                      <div className="d-flex justify-content-end mb-2">
                        <Button onClick={() => addDomainToGroup(key)} color="light" title="Add domain">
                          <i className="icon-plus" />
                        </Button>
                      </div>
                    )}
                    {liveRows.map((row) => renderDomainRow(row, key))}
                    {liveRows.length === 0 && (
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        No live domains yet — click + to add one.
                      </div>
                    )}
                    {key && persistedFrontendDomains.some((d) => d.frontend === key) && (
                      <div className="mt-2">
                        {persistedFrontendDomains
                          .filter((d) => d.frontend === key)
                          .map((d) => (
                            <div key={d.name}>
                              <Button
                                color="link"
                                className="p-0 align-baseline"
                                href={'https://' + d.name}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: '13px' }}
                              >
                                https://{d.name}
                              </Button>{' '}
                              <span className={'badge ' + v5StatusBadge(v5StatusText(d.name))}>{v5StatusText(d.name)}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}

            {/* staging hostname prompt for a new grouping */}
            {groupPrompt && (
              <div
                onClick={() => setGroupPrompt(null)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(30,40,50,.45)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1080,
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded shadow"
                  style={{ width: '420px', maxWidth: '92%', overflow: 'hidden' }}
                >
                  <div className="px-4 pt-3">
                    <div className="fw-bold" style={{ fontSize: '17px' }}>
                      New {frontendLabel(groupPrompt)} grouping
                    </div>
                    <small className="text-muted">
                      Enter the staging hostname. The full staging domain is generated automatically.
                    </small>
                  </div>
                  <div className="px-4 py-3">
                    <label className="text-muted text-uppercase d-block mb-1" style={{ fontSize: '12px', letterSpacing: '.5px' }}>
                      Staging hostname
                    </label>
                    <div className="d-flex border rounded" style={{ overflow: 'hidden' }}>
                      <span className="text-muted px-2 py-2" style={{ background: '#dfe4e7', fontSize: '13px' }}>
                        https://
                      </span>
                      <input
                        value={promptHost}
                        onChange={(e) => setPromptHost(e.target.value.replace(/\./g, ''))}
                        placeholder="mystore"
                        className="border-0 px-2 py-2 flex-grow-1"
                        style={{ outline: 'none', minWidth: 0 }}
                      />
                      <span className="text-muted px-2 py-2" style={{ background: '#f2f4f5', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        .{stagingSuffix}
                      </span>
                    </div>
                    {promptHost.trim() && promptHostError && <ErrorMessage error={promptHostError} />}
                  </div>
                  <div className="border-top px-4 py-3 d-flex justify-content-end gap-2" style={{ background: '#fafbfc' }}>
                    <Button color="secondary" type="button" onClick={() => setGroupPrompt(null)}>
                      Cancel
                    </Button>
                    <Button color="primary" type="button" disabled={!promptHost.trim() || !!promptHostError} onClick={createGrouping}>
                      Create grouping
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormGroup row>
            <Col md="3">
              <label htmlFor="erpType">ERP</label>
            </Col>
            <Col md="5">
              <select
                {...erpTypeReg}
                onChange={(e) => {
                  erpTypeReg.onChange(e);
                  onChangeErpType(e);
                }}
                className="form-control"
                disabled={isEdit}
                required
              >
                <option />
                {erpType.all.map((type) => {
                  return (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  );
                })}
              </select>
            </Col>
            <Col md="4">
              <select {...register('erpTypeVersion.id', { required: true })} className="form-control" required>
                <option />
                {(erpTypeVersions || []).map((version) => {
                  return (
                    <option key={version.id} value={version.id}>
                      {version.name}
                    </option>
                  );
                })}
              </select>
            </Col>
          </FormGroup>
          <h5>Contact Person</h5>
          <hr className="separator" />
          <FormGroup row>
            <Col md="3">
              <label htmlFor="contactName">Name</label>
            </Col>
            <Col md="9">
              <input {...register('contactName')} type="text" className="form-control" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="contactEmail">Email</label>
            </Col>
            <Col md="9">
              <input {...register('contactEmail')} type="text" className="form-control" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="contactPhone">Phone</label>
            </Col>
            <Col md="9">
              <input {...register('contactPhone')} type="text" className="form-control" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="partnerUsers">Partners</label>
            </Col>
            <Col md="9">
              <RHFField
                name="partnerUsers"
                control={control}
                component={SelectField}
                options={operator.all}
                isMulti={true}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="3">
              <label htmlFor="storeType">Store Type</label>
            </Col>
            <Col md="9">
              <select {...register('storeType', { required: true })} className="form-control" required>
                <option />
                {config.enums &&
                  config.enums['storeType'].map((type, key) => {
                    return (
                      <option key={key} value={type}>
                        {capitalizeFirstLetter(type)}
                      </option>
                    );
                  })}
              </select>
            </Col>
          </FormGroup>
          {/* PDECOM-6293: either flag exempts the store from the staging retention policy, which
              otherwise deactivates a store nobody has logged into for 90 days and later deletes it. */}
          <FormGroup row>
            <Col md="3">
              <label htmlFor="paidClientSandbox">Retention</label>
            </Col>
            <Col md="9">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="paidClientSandbox"
                  className="form-check-input"
                  {...register('isPaidClientSandbox')}
                />
                <label className="form-check-label" htmlFor="paidClientSandbox">
                  Paid-for client sandbox
                </label>
              </div>
              <div className="form-check">
                <input type="checkbox" id="woocommerce" className="form-check-input" {...register('isWoocommerce')} />
                <label className="form-check-label" htmlFor="woocommerce">
                  WooCommerce store
                </label>
              </div>
              <span className="small text-muted">
                Either flag exempts this store from automatic deactivation and deletion on staging.
              </span>
            </Col>
          </FormGroup>
          {/* repeated by the buttons: the form is taller than the modal, so an error at the
              very top is off-screen from where Save was clicked */}
          {error && <Alert color="danger">{error}</Alert>}
          <div className="float-end">
            <Button type="button" color="secondary" className="me-2" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Save
            </Button>
          </div>
        </form>
      </div>
      {isEdit ? (
        <div style={{ paddingTop: '3rem' }}>
          <Card>
            <CardBody>
              <XMTable headers={[{ name: 'Status' }, { name: 'User' }, { name: 'When' }]} autoRenderRow={true}>
                {(one.storeStatuses || []).map((ss) => (
                  <React.Fragment key={ss.id}>
                    <td className="align-middle">
                      <span>{ss.statusType}</span>
                    </td>
                    <td className="align-middle">
                      <span>{ss.createdBy}</span>
                    </td>
                    <td className="align-middle">
                      <Moment format="Do MMM YYYY, HH:mm" withTitle>
                        {ss.createdDate}
                      </Moment>
                    </td>
                  </React.Fragment>
                ))}
              </XMTable>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

export default StoreForm;
